import React, { useState, useEffect } from 'react';
import { Project, SectionId } from '../types';
import { FadeIn } from './ui/FadeIn';
import { Play } from 'lucide-react';
import { VideoModal } from './ui/VideoModal';

// Wedding Films only - 16:9 cinematic format
const weddingFilms: Project[] = [
  {
    id: 1,
    title: "Abhijeet & Arushi",
    location: "Rambagh Palace, Jaipur",
    category: "Film",
    image: "", // Will be fetched from Vimeo
    videoId: "1139757776",
    videoPlatform: "vimeo"
  },
  {
    id: 2,
    title: "Aniket & Isha",
    location: "Taj Lake Palace, Udaipur",
    category: "Film",
    image: "",
    videoId: "1139757975",
    videoPlatform: "vimeo"
  },
  {
    id: 3,
    title: "Harsh & Stuti",
    location: "Umaid Bhawan, Jodhpur",
    category: "Film",
    image: "",
    videoId: "1139758344",
    videoPlatform: "vimeo"
  },
  {
    id: 4,
    title: "Gauri & Aditya",
    location: "The Oberoi, New Delhi",
    category: "Film",
    image: "",
    videoId: "1139758283",
    videoPlatform: "vimeo"
  },
  {
    id: 5,
    title: "Aniket & Esha",
    location: "Fairmont, Jaipur",
    category: "Film",
    image: "",
    videoId: "1139757930",
    videoPlatform: "vimeo"
  }
];

export const Work: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>(weddingFilms);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loadingThumbnails, setLoadingThumbnails] = useState(true);

  // Fetch High-Quality Vimeo Thumbnails
  useEffect(() => {
    const fetchThumbnails = async () => {
      setLoadingThumbnails(true);
      const updatedProjects = await Promise.all(
        weddingFilms.map(async (project) => {
          if (project.videoPlatform === 'vimeo' && project.videoId) {
            try {
              // Use Vimeo oEmbed API with specific width for high quality
              const response = await fetch(
                `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${project.videoId}&width=1920`
              );
              const data = await response.json();
              if (data.thumbnail_url) {
                // Get the highest resolution thumbnail
                // Vimeo thumbnails have format: ...d_640.jpg - replace with higher res
                let highResThumb = data.thumbnail_url;
                // Try to get 1920px wide thumbnail
                highResThumb = highResThumb.replace(/_\d+x\d+/, '_1920x1080');
                highResThumb = highResThumb.replace(/-d_\d+/, '-d_1920');
                return { ...project, image: highResThumb };
              }
            } catch (error) {
              console.error(`Failed to fetch thumbnail for ${project.title}`, error);
            }
          }
          return project;
        })
      );
      setProjects(updatedProjects);
      setLoadingThumbnails(false);
    };

    fetchThumbnails();
  }, []);

  const handleProjectClick = (project: Project) => {
    if (project.videoId) {
      setSelectedProject(project);
    }
  };

  return (
    <>
      <section id={SectionId.WORK} className="py-24 md:py-32 bg-stone-50">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12">
          <FadeIn className="mb-16 text-center">
            <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 mb-4">Our Films</p>
            <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-4">Visual Narratives</h2>
            <div className="w-12 h-[1px] bg-stone-300 mx-auto"></div>
          </FadeIn>

          {/* Cinematic Film Grid - 16:9 Aspect Ratio */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {projects.map((project, index) => (
              <FadeIn key={project.id} delay={index * 100}>
                <div 
                  className="group cursor-pointer"
                  onClick={() => handleProjectClick(project)}
                >
                  {/* 16:9 Aspect Ratio Container */}
                  <div className="relative overflow-hidden aspect-video bg-stone-900 shadow-xl">
                    {/* Loading State */}
                    {loadingThumbnails && (
                      <div className="absolute inset-0 bg-stone-800 animate-pulse flex items-center justify-center">
                        <div className="w-8 h-8 border-2 border-stone-600 border-t-stone-400 rounded-full animate-spin" />
                      </div>
                    )}
                    
                    {/* Thumbnail Image */}
                    {project.image && (
                      <img 
                        src={project.image} 
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-105"
                        loading="lazy"
                      />
                    )}
                    
                    {/* Cinematic Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
                    
                    {/* Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center z-20">
                      <div className="bg-white/10 backdrop-blur-sm p-5 rounded-full border border-white/20 transform scale-100 group-hover:scale-110 group-hover:bg-white/20 transition-all duration-500 shadow-2xl">
                        <Play size={28} className="text-white ml-1" fill="white" strokeWidth={0} />
                      </div>
                    </div>
                    
                    {/* Film Badge */}
                    <div className="absolute top-4 left-4 z-20">
                      <span className="text-[9px] uppercase tracking-widest text-white/70 bg-black/30 backdrop-blur-sm px-3 py-1">
                        Wedding Film
                      </span>
                    </div>
                    
                    {/* Title Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                      <p className="text-[10px] tracking-[0.2em] uppercase text-white/60 mb-1">
                        {project.location}
                      </p>
                      <h3 className="text-2xl md:text-3xl font-serif text-white italic">
                        {project.title}
                      </h3>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal 
        isOpen={!!selectedProject} 
        onClose={() => setSelectedProject(null)} 
        videoId={selectedProject?.videoId || ''}
        platform={selectedProject?.videoPlatform || 'vimeo'}
        title={selectedProject?.title || ''}
      />
    </>
  );
};